import React, { useState, useEffect, useMemo, useRef } from "react";
import { useSelector, useDispatch } from 'react-redux';
import {
    setActiveSearch,
    clearActiveSearch,
    clearSLRContext,
} from '../../../../store/literatureDiscoverySlice';
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { TopNav, SideNav } from "../../navigation/ConsoleNavigation";
import GetCookie from "../../../utils/GetCookie";
import authNavigate from "../../../globalComponents/AuthNav";
import ErrorPopup from "../../../utils/popup/ErrorPopup";
import SuccessPopup from "../../../utils/popup/SuccessPopup";
import ProcessingPopup from "../../../utils/popup/ProcessingPopup";

import LiteratureSearchesList from "./LiteratureSearchesList";
import CreateLiteratureSearchForm from "./CreateLiteratureSearchForm";
import CreateLiteratureSearchModal from "./CreateLiteratureSearchModal";
import LiteratureDiscoveryDashboard from "./LiteratureDiscoveryDashboard";
import FDADiscoveryDashboard from "./fda/FDADiscoveryDashboard";
import ClinicalTrialsDiscoveryDashboard from "./clinicaltrials/ClinicalTrialsDiscoveryDashboard";
import EMADiscoveryDashboard from "./ema/EMADiscoveryDashboard";

const LiteratureDiscovery = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();

    // ═══ Redux-persisted state (survives page navigation) ═══
    const { activeSearch: reduxActiveSearch, slrContext } = useSelector((state) => state.literatureDiscovery);

    // ═══ If we arrived with a linkedSearch in navigation state, use it immediately
    //     so the dashboard opens without waiting for Redux to hydrate ═══
    const [localActiveSearch, setLocalActiveSearch] = useState(
        () => location.state?.linkedSearch || null
    );

    // ═══ Capture SLR origin context from nav state before it gets cleared ═══
    //     Used to redirect Cancel (form) and Back (dashboard) to Project Overview
    const [fromSLRState] = useState(
        () => location.state?.fromSLR
            ? {
                fromSLR: true,
                slrFabricId: location.state.slrFabricId,
                slrProjectName: location.state.slrProjectName,
              }
            : null
    );

    const [fromLinkFullText] = useState(() => location.state?.fromLinkFullText || false);
    const [lfReturnPath] = useState(() => location.state?.returnPath || null);
    const [slrReturnPath] = useState(() => location.state?.fromSLR ? (location.state?.returnPath || null) : null);

    // Merge: prefer localActiveSearch (set from nav state) falling back to Redux
    const activeSearch = localActiveSearch || reduxActiveSearch;

    // ═══ Local transient state ═══
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [size, setSize] = useState(25);
    const [search, setSearch] = useState("");
    const [searches, setSearches] = useState([]);

    const [refreshTrigger, setRefreshTrigger] = useState(0);

    // ═══ Full-page create search view (PubMed) ═══
    const [isCreateSearchView, setIsCreateSearchView] = useState(false);
    const [editingSearch, setEditingSearch] = useState(null);

    // ═══ Source picker dropdown + FDA/CT modal ═══
    const [sourcePickerOpen, setSourcePickerOpen] = useState(false);
    const [createModalSource, setCreateModalSource] = useState(null); // "FDA" | "CLINICAL_TRIAL"
    const newSearchRef = useRef(null);
    const eventSourceRef = useRef(null);

    const [isProcessingPopupVisible, setIsProcessingPopupVisible] = useState(false);
    const [processingMessage, setProcessingMessage] = useState("");
    const [isErrorPopupVisible, setIsErrorPopupVisible] = useState(false);
    const [popupErrorMessage, setPopupErrorMessage] = useState("");
    const [isSuccessPopupVisible, setIsSuccessPopupVisible] = useState(false);
    const [popupSuccessMessage, setPopupSuccessMessage] = useState("");

    const API_BASE_URL = "/pceapi/slr/discovery";

    const headers = useMemo(() => ({
        Authorization: GetCookie("access_token"),
    }), []);

    const closeErrorPopup = () => setIsErrorPopupVisible(false);
    const closeSuccessPopup = () => setIsSuccessPopupVisible(false);

    const fetchSearches = async () => {
        setIsProcessingPopupVisible(true);
        setProcessingMessage("Loading Searches...");

        try {
            const response = await axios.post(
                `${API_BASE_URL}/list`,
                { page, size, search },
                { headers }
            );

            const data = response.data;

            if (data.status === "NotAuthorized") {
                authNavigate('/login', 'iam', navigate);
                return;
            }

            if (data.status === "failed") {
                setPopupErrorMessage(data.message || "Failed to load searches");
                setIsErrorPopupVisible(true);
                return;
            }

            if (data.status === "success") {
                setSearches(data.data || []);
                setTotal(data.total || 0);
            }
        } catch (error) {
            if (error.response?.data?.status === "NotAuthorized") {
                authNavigate('/login', 'iam', navigate);
                return;
            }

            const errorMsg = error.response?.data?.message
                || error?.message
                || "Failed to fetch searches";

            setPopupErrorMessage(errorMsg);
            setIsErrorPopupVisible(true);
        } finally {
            setIsProcessingPopupVisible(false);
            setProcessingMessage("");
        }
    };

    const startSSEStream = (discoveryId) => {
    if (eventSourceRef.current) {
        eventSourceRef.current.abort();
    }

    const controller = new AbortController();
    eventSourceRef.current = controller;

    fetch(`${API_BASE_URL}/status-stream`, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            Authorization: GetCookie("access_token"),
        },
        body: JSON.stringify({ discovery_id: discoveryId }),
        signal: controller.signal,
    })
    .then((response) => {
        const reader = response.body.getReader();
        const decoder = new TextDecoder();

        const read = () => {
            reader.read().then(({ done, value }) => {
                if (done) return;

                const text = decoder.decode(value, { stream: true });
                const lines = text.split("\n");

                lines.forEach((line) => {
                    if (line.startsWith("data:")) {
                        try {
                            const data = JSON.parse(line.replace("data:", "").trim());
                            if (data.type === "progress") {
                                // update progress in UI
                            }
                            if (data.type === "status") {
                                if (data.status === "COMPLETED" || data.status === "FAILED") {
                                    controller.abort();
                                    eventSourceRef.current = null;
                                    setRefreshTrigger((prev) => prev + 1);
                                }
                            }
                        } catch (e) {
                            // skip malformed line
                        }
                    }
                });
                read();
            }).catch(() => {
                eventSourceRef.current = null;
            });
        };
        read();
    })
    .catch(() => {
        eventSourceRef.current = null;
    });
};

    const handleDeleteSearch = async (searchObj) => {
        try {
            const res = await axios.post(
                `${API_BASE_URL}/delete`,
                { discovery_id: searchObj.id },
                { headers }
            );

            if (res.data?.status === "NotAuthorized") {
                authNavigate('/login', 'iam', navigate);
                return;
            }

            if (res.data?.status !== "success") {
                throw new Error(res.data?.message || "Failed to delete");
            }

            setRefreshTrigger((prev) => prev + 1);
        } catch (error) {
            if (error.response?.data?.status === "NotAuthorized") {
                authNavigate('/login', 'iam', navigate);
                return;
            }

            console.error("Delete failed", error);
            throw error; 
        }
    };

    const handleCreateSuccess = (createdSearch) => {
        setIsCreateSearchView(false);
        setEditingSearch(null);
        setCreateModalSource(null);
        setSourcePickerOpen(false);
        if (createdSearch && createdSearch.id) {
            dispatch(setActiveSearch(createdSearch));
        } else {
            dispatch(clearActiveSearch());
            setPage(1);
            setRefreshTrigger((prev) => prev + 1);
        }
    };

    // Close source picker / modal on outside click
    useEffect(() => {
        const handler = (e) => {
            if (newSearchRef.current && !newSearchRef.current.contains(e.target)) {
                setSourcePickerOpen(false);
                setCreateModalSource(null);
            }
        };
        if (sourcePickerOpen || createModalSource) {
            document.addEventListener("mousedown", handler);
        }
        return () => document.removeEventListener("mousedown", handler);
    }, [sourcePickerOpen, createModalSource]);

    useEffect(() => {
        if (!activeSearch && !isCreateSearchView) {
            fetchSearches();
        }
    }, [page, size, search, refreshTrigger, activeSearch, isCreateSearchView]);

    // ═══ Auto-open create form when redirected from SLR overview ═══
    useEffect(() => {
        // openCreateForm means "start fresh" — it must clear whatever's
        // already open, not require it to already be clear.
        if (location.state?.openCreateForm) {
            dispatch(clearActiveSearch());
            dispatch(clearSLRContext());
            setIsCreateSearchView(true);
            navigate(location.pathname, { replace: true, state: {} });
        }
    }, [location.state?.openCreateForm]);

    // ═══ Clear nav state after seeding localActiveSearch so back-navigation is clean ═══
    useEffect(() => {
        if (location.state?.linkedSearch) {
            navigate(location.pathname, { replace: true, state: {} });
        }
    }, []);

    useEffect(() => {
        return () => {
            if (eventSourceRef.current) {
                eventSourceRef.current.abort();
                eventSourceRef.current = null;
            }
        };
    }, []);

    /* ─── Determine what content to show in the main area ─── */
    const renderMainContent = () => {
        // 1) Full-page create search form (new or edit)
        if (isCreateSearchView && !activeSearch) {
            return (
                <CreateLiteratureSearchForm
                    onClose={() => {
                        // If opened from SLR Project Overview and not editing,
                        // Cancel should take the user back to the overview page
                        if (fromSLRState?.fromSLR && !editingSearch) {
                            navigate(slrReturnPath || -1, { state: { restoreOverview: true } });
                            return;
                        }
                        setIsCreateSearchView(false);
                        // If editing, restore the dashboard for that search
                        if (editingSearch) {
                            dispatch(setActiveSearch(editingSearch));
                            setEditingSearch(null);
                        }
                    }}
                    onSuccess={handleCreateSuccess}
                    editSearch={editingSearch}
                    slrFabricId={fromSLRState?.slrFabricId}
                />
            );
        }

        // 2) Dashboard view (existing behavior)
        if (activeSearch) {
            if (activeSearch.source_type === "FDA") {
                return (
                    <FDADiscoveryDashboard
                        search={activeSearch}
                        onBack={() => {
                            setLocalActiveSearch(null);
                            dispatch(clearActiveSearch());
                            dispatch(clearSLRContext());
                        }}
                    />
                );
            }
            if (activeSearch.source_type === "CLINICAL_TRIAL") {
                return (
                    <ClinicalTrialsDiscoveryDashboard
                        search={activeSearch}
                        onBack={() => {
                            setLocalActiveSearch(null);
                            dispatch(clearActiveSearch());
                            dispatch(clearSLRContext());
                        }}
                    />
                );
            }
            if (activeSearch.source_type === "EMA") {
                return (
                    <EMADiscoveryDashboard
                        search={activeSearch}
                        onBack={() => {
                            dispatch(clearActiveSearch());
                            dispatch(clearSLRContext());
                        }}
                    />
                );
            }
            return (
                <LiteratureDiscoveryDashboard
                    search={activeSearch}
                    slrContext={slrContext}
                    backLabel={
                        fromLinkFullText
                            ? "Back to Screening"
                            : fromSLRState?.fromSLR
                                ? "Back to Project Overview"
                                : undefined
                    }
                    onBack={() => {
                        setLocalActiveSearch(null);
                        dispatch(clearActiveSearch());
                        dispatch(clearSLRContext());
                        if (fromLinkFullText) {
                            navigate(lfReturnPath || -1, { state: { restoreLFView: true } });
                            return;
                        }
                        if (fromSLRState?.fromSLR) {
                            navigate(-1);
                            return;
                        }
                    }}
                    onEdit={async (searchObj) => {
                        // The object reaching us here comes from three different
                        // places (list row, freshly-saved response, nav state) and
                        // only some of them carry form_answers / search_query.
                        let full = searchObj;
                        try {
                            const res = await axios.post(
                                `/pceapi/slr/discovery/list`,
                                { page: 1, size: 1, search_id: searchObj.id },
                                { headers: { Authorization: GetCookie("access_token") } }
                            );
                            const row = res.data?.data?.[0];
                            if (row?.id === searchObj.id) {
                                full = { ...searchObj, ...row };
                            }
                        } catch (e) {
                            console.error("Could not refresh search before edit", e);
                        }
                        setLocalActiveSearch(null);
                        setEditingSearch(full);
                        dispatch(clearActiveSearch());
                        dispatch(clearSLRContext());
                        setIsCreateSearchView(true);
                    }}
                />
            );
        }

        // 3) Search list (default)
        return (
            <div className="m-4">
                    <p className="ev-med-p-dark-blue mb-2">
                        Literature Searches ({total})
                    </p>
                    <LiteratureSearchesList
                        searches={searches}
                        total={total}
                        page={page}
                        setPage={setPage}
                        size={size}
                        setSize={setSize}
                        search={search}
                        setSearch={setSearch}
                        onRefresh={() => setRefreshTrigger((p) => p + 1)}
                        onOpenDashboard={(row) => {
                            dispatch(setActiveSearch(row));
                            startSSEStream(row.id);
                        }}
                        onDeleteSearch={handleDeleteSearch}
                    />
                </div>
        );
    };

    return (
        <div className="main-window">
            <TopNav />

            <div className="container-fluid">
                <div className="row">
                    <SideNav />

                    <main className="col-12 col-md-9 col-lg-10 ms-sm-auto console-main-window">
                        <div className="">
                            {/* Header — always visible */}
                            <div className="ev-card-2 dashboard-header position-static mb-2 w-100">
                                <div></div>
                                <p className="ev-med-p-dark mb-0 ev-bold-reg">Literature Discovery</p>
                                {!isCreateSearchView ? (
                                    <div className="position-relative" ref={newSearchRef}>
                                        <button
                                            type="button"
                                            className="ev-btn-yellow-fill-round ev-btn-reg ev-bold-reg"
                                            onClick={() => setSourcePickerOpen((prev) => !prev)}
                                        >
                                            <i className="bi bi-plus-circle me-1"></i> New Search
                                            <i className={`bi ${sourcePickerOpen ? "bi-chevron-up" : "bi-chevron-down"} ms-1`}></i>
                                        </button>

                                        {/* Source picker dropdown */}
                                        {sourcePickerOpen && !createModalSource && (
                                            <div className="ev-card-4 position-absolute end-0 mt-1 ev-radius-reg ev-padding-small create-search-dropdown z-3">
                                                <p className="ev-small-p-dark ev-bold-med mb-2">Select search type</p>

                                                {/* PubMed is the only source that produces title/abstract
                                                    records — it is the only one a systematic review's
                                                    screening pipeline can use. */}
                                                <button
                                                    type="button"
                                                    className="w-100 text-start d-flex align-items-center gap-3 ev-btn-light-fill ev-radius-xsmall mb-1 ev-padding-small border border-2"
                                                    style={{ borderColor: "var(--color2)" }}
                                                    onClick={() => {
                                                        setSourcePickerOpen(false);
                                                        dispatch(clearActiveSearch());
                                                        dispatch(clearSLRContext());
                                                        setIsCreateSearchView(true);
                                                    }}
                                                >
                                                    <i className="bi bi-journal-medical ev-fs-18 ev-reg-p-blue flex-shrink-0"></i>
                                                    <div>
                                                        <p className="ev-small-p-dark ev-bold-med mb-0 d-flex align-items-center gap-2">
                                                            Create Search Query 
                                                            <span className="ev-message-card3 ev-btn-xsmall">For Systematic Reviews</span>
                                                        </p>
                                                        <p className="ev-xsmall-p-dark mb-0">Structured PICOS search builder for - PubMed, Embase, Cochrane</p>
                                                    </div>
                                                </button>

                                                <p className="ev-xsmall-p-dark opacity-75 mt-2 mb-1 ms-1">Other sources (not used for SLR screening)</p>
                                                {[
                                                    { id: "FDA", label: "FDA", icon: "bi-capsule", desc: "FDA drug applications — regulatory research" },
                                                    { id: "CLINICAL_TRIAL", label: "Clinical Trials", icon: "bi-clipboard2-pulse", desc: "ClinicalTrials.gov search — trial landscape research" },
                                                    { id: "EMA", label: "EMA", icon: "bi-globe", desc: "European Medicines Agency — regulatory research" },
                                                ].map((opt) => (
                                                    <button
                                                        key={opt.id}
                                                        type="button"
                                                        className="w-100 text-start d-flex align-items-center gap-3 ev-btn-light-fill ev-radius-xsmall mb-1 ev-padding-small"
                                                        onClick={() => {
                                                            setSourcePickerOpen(false);
                                                            setCreateModalSource(opt.id);
                                                        }}
                                                    >
                                                        <i className={`bi ${opt.icon} ev-fs-18 ev-reg-p-blue flex-shrink-0`}></i>
                                                        <div>
                                                            <p className="ev-small-p-dark ev-bold-med mb-0">{opt.label}</p>
                                                            <p className="ev-xsmall-p-dark mb-0">{opt.desc}</p>
                                                        </div>
                                                    </button>
                                                ))}
                                            </div>
                                        )}

                                        {/* FDA / Clinical Trial modal */}
                                        {createModalSource && (
                                            <CreateLiteratureSearchModal
                                                defaultSourceType={createModalSource}
                                                onClose={() => setCreateModalSource(null)}
                                                onSuccess={handleCreateSuccess}
                                                onSwitchToPubMed={() => {
                                                    setCreateModalSource(null);
                                                    dispatch(clearActiveSearch());
                                                    dispatch(clearSLRContext());
                                                    setIsCreateSearchView(true);
                                                }}
                                            />
                                        )}
                                    </div>
                                ) : (
                                    <div></div>
                                )}
                            </div>

                            {renderMainContent()}
                        </div>
                    </main>
                </div>
            </div>

            {isProcessingPopupVisible && (
                <ProcessingPopup message={processingMessage || "Processing..."} />
            )}

            {isErrorPopupVisible && (
                <ErrorPopup
                    onClose={closeErrorPopup}
                    message={popupErrorMessage}
                />
            )}

            {isSuccessPopupVisible && (
                <SuccessPopup
                    onClose={closeSuccessPopup}
                    message={popupSuccessMessage}
                />
            )}
        </div>
    );
};

export default LiteratureDiscovery;